/**
 * Handles submenu positioning based on available viewport space
 * Automatically adjusts both vertical (top/bottom/center) and horizontal (left/right) positioning
 * to prevent submenus from being cut off by viewport edges
 *
 * Vertical positioning logic:
 * - If fits below: position below (default)
 * - If doesn't fit below but fits above: position above
 * - If doesn't fit either above or below: center vertically
 *
 * Horizontal positioning logic:
 * - If fits on preferred side: keep current position
 * - If doesn't fit on current side but fits on other: switch sides
 * - If doesn't fit either side: choose side with more space
 *
 * @param {HTMLElement} node - The submenu DOM node
 * @param {string} submenuKey - Unique key for the submenu
 * @param {Object} submenuRefs - Ref object to store submenu references
 */
export const handleSubmenuPositioning = (node, submenuKey, submenuRefs) => {
  submenuRefs.current[submenuKey] = node;

  if (!node) return;

  // Check position after DOM is ready
  setTimeout(() => {
    // Safety checks to prevent errors
    if (!node || !node.parentElement) {
      console.warn('Submenu node or parent not available for positioning');
      return;
    }

    try {
      const rect = node.getBoundingClientRect();
      const parentRect = node.parentElement.getBoundingClientRect();

      // Additional safety check for valid rectangles
      if (!rect || !parentRect || rect.height === 0) {
        console.warn('Invalid rectangles for submenu positioning');
        return;
      }

      let currentClass = node.className;

      // VERTICAL POSITIONING
      // Calculate available space above and below
      const spaceBelow = window.innerHeight - parentRect.bottom;
      const spaceAbove = parentRect.top;
      const submenuHeight = rect.height;

      // Check if submenu fits in either direction
      const fitsBelow = spaceBelow >= submenuHeight;
      const fitsAbove = spaceAbove >= submenuHeight;

      // Determine vertical positioning
      if (!fitsBelow && !fitsAbove) {
        // Doesn't fit either above or below - center it vertically
        currentClass = currentClass.replace('-top-[14px]', '').replace('-bottom-[8px]', '');

        // Add centering classes if not already present
        if (!currentClass.includes('top-1/2')) {
          currentClass += ' top-1/2 -translate-y-1/2';
        }
      } else if (!fitsBelow && fitsAbove) {
        // Doesn't fit below but fits above - position above
        if (!currentClass.includes('-bottom-[8px]')) {
          currentClass = currentClass.replace('-top-[14px]', '-bottom-[8px]');
        }
        // Remove centering classes
        currentClass = currentClass.replace('top-1/2', '').replace('-translate-y-1/2', '').trim();
      } else {
        // Fits below (default) or both directions fit - position below
        if (!currentClass.includes('-top-[14px]')) {
          currentClass = currentClass.replace('-bottom-[8px]', '-top-[14px]');
        }
        // Remove centering classes
        currentClass = currentClass.replace('top-1/2', '').replace('-translate-y-1/2', '').trim();
      }

      // HORIZONTAL POSITIONING
      // Calculate available space on both sides
      const spaceRight = window.innerWidth - parentRect.right;
      const spaceLeft = parentRect.left;
      const submenuWidth = rect.width;

      // Add some padding to avoid edge collisions
      const fitsOnRight = spaceRight >= submenuWidth;
      const fitsOnLeft = spaceLeft >= submenuWidth;

      // Current horizontal position
      const isCurrentlyRight = currentClass.includes('left-full');
      const isCurrentlyLeft = currentClass.includes('right-full');

      // Determine the best horizontal position
      let shouldSwitchToLeft = false;
      let shouldSwitchToRight = false;

      if (isCurrentlyRight && !fitsOnRight && fitsOnLeft) {
        // Currently right, doesn't fit right, but fits left
        shouldSwitchToLeft = true;
      } else if (isCurrentlyLeft && !fitsOnLeft && fitsOnRight) {
        // Currently left, doesn't fit left, but fits right
        shouldSwitchToRight = true;
      } else if (!fitsOnRight && !fitsOnLeft) {
        // Doesn't fit either side, choose side with more space
        if (spaceRight > spaceLeft) {
          shouldSwitchToRight = true;
        } else {
          shouldSwitchToLeft = true;
        }
      }

      // Apply horizontal position changes
      if (shouldSwitchToLeft && !isCurrentlyLeft) {
        currentClass = currentClass.replace('left-full', 'right-full');
      } else if (shouldSwitchToRight && !isCurrentlyRight) {
        currentClass = currentClass.replace('right-full', 'left-full');
      }

      // Apply the updated classes
      node.className = currentClass;
    } catch (error) {
      console.warn('Error during submenu positioning:', error);
    }
  }, 0);
};

/**
 * Generates a unique key for submenu based on its path
 * @param {string} parentPath - Path from parent levels
 * @param {number} level - Current nesting level
 * @param {number} index - Index within current level
 * @returns {string} Unique submenu key
 */
export const generateSubmenuKey = (parentPath, level, index) => {
  return parentPath ? `${parentPath}-${index}` : `${level}-${index}`;
};
